import { all, call, CallEffect, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { CompositeSessionInfoSelectors } from '~selectors/SessionInfoSelectors';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { selectContentCountsUpToDate } from '~src/spacesPlatform/selectors/ContentCountsSelector';
import { selectMixedContentFilterClauses } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import {
  DataRoleSubspaceName,
  DatasourceSubspaceName,
  isCatalogSpace,
  isDataRoleSpace,
  isDatasourceSpace,
  isFavoritesSpace,
  isPersonalSpace,
  isProjectSpace,
  isPublishedConnectionSpace,
  isPublishedConnectionTableSpace,
  isScheduleSpace,
  isSearchSpace,
  isServerTasksSpace,
  isSharedWithMeSpace,
  isSingleCollectionSpace,
  isSiteSpace,
  isSiteTasksSpace,
  isSiteUserSpace,
  isWorkbookSpace,
  PublishedConnectionSubspaceName,
  PublishedConnectionTableSubspaceName,
  ScheduleSubspaceName,
  SearchSubspaceName,
  ServerTasksSubspaceName,
  SiteTasksSubspaceName,
  SiteUserSubspaceName,
  SpaceInfo,
  SubspaceName,
  WorkbookSubspaceName,
} from '~src/spacesPlatform/state/SpaceInfo';

import { requestFailed } from '../../../app/actions/RequestFailedAction';
import { SiteSpaceContentType } from '../../../app/state/SiteSpaceContentTypeState';
import { selectCatalogingOnSiteEnabled } from '../../../catalog/selectors/CatalogSelectors';
import { Messages } from '../../../codegen/Localize';
import { selectFlowsOnSiteEnabled } from '../../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IResourceQuery } from '../../../ts/resources/ResourceQuery';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { IStringValuedFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import {
  ContentCountsRequestAction,
  contentCountsRequestSucceeded,
} from '../../actions/ContentCountsActions';
import {
  SpaceInfoTypeContentCount,
  SpaceInfoTypeContentCounts,
} from '../../state/ContentCountsState';
import { FilterMap } from '../../state/ContentExplorerState';
import {
  getCollectionItemsResourceQuery,
  getFavoritesResourceQueryForContentTypes,
  getPersonalSpaceResourceQueryForContentTypes,
  getProjectOrSiteResourceQueryForContentTypes,
  getResourceQuery,
  getSharedWithMeResourceQuery,
  getSiteResourceQueryForCatalogSpaceContentType,
  getSiteResourceQueryForSiteSpaceContentType,
  getSiteUser_ContentSubspace_ResourceQueryForContentTypes,
  getSiteUser_ResourceQueryForSpaceTypes,
} from './MixedContentSagas';

export function* processContentCountRequest(
  resource: IResourceQuery<any>
): any {
  try {
    if (_.isUndefined(resource)) {
      return 0;
    }
    resource = resource.withOrder([]); // ignore any sort order

    const count: number = yield call(fetchCountForContent, resource);
    return count;
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// For the counts, all we need is the "totalCount", so just request zero items
export function fetchCountForContent<T>(
  resource: IResourceQuery<T>
): Promise<number> | undefined {
  return resource.getCount();
}

export function* processContentCountRequestForFavorites(
  contentType: ExploreContentApi_ContentType
) {
  const resource = yield call(getFavoritesResourceQueryForContentTypes, [
    contentType,
  ]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForProject(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getProjectOrSiteResourceQueryForContentTypes, [
    contentType,
  ]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSharedWithMe(
  contentType: ExploreContentApi_ContentType
) {
  // In SWM space, we need count for each content type specifically so adding filter clause to the request
  // TODO: This needs to be fixed.
  // We would need to change the pattern here because all other spaces, e.g. favourites, explore, push out
  // multiple requests like getProject, getViews, getWorkbook when you open the contenttype filter
  // In SwM case, we need to push multiple getSharedWithMe requests with filter value updated
  // TFS 1107593
  const resource = yield call(getSharedWithMeResourceQuery, [contentType]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSiteUserCollections(
  spaceInfo: SpaceInfo
) {
  const resource = yield call(
    getSiteUser_ResourceQueryForSpaceTypes,
    spaceInfo
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSiteUserContentType(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(
    getSiteUser_ContentSubspace_ResourceQueryForContentTypes,
    [contentType]
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForPersonalSpaceContentType(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getPersonalSpaceResourceQueryForContentTypes, [
    contentType,
  ]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForCollectionSpaceContentType(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getCollectionItemsResourceQuery, [contentType]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSite(
  siteSpaceContentType: SiteSpaceContentType
) {
  // The counts for the content types need to look specifically at each site content type,
  const resource = yield call(
    getSiteResourceQueryForSiteSpaceContentType,
    siteSpaceContentType
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForCatalog(
  siteSpaceContentType: SiteSpaceContentType
) {
  const resource = yield call(
    getSiteResourceQueryForCatalogSpaceContentType,
    siteSpaceContentType
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSpaceInfo(spaceInfo: SpaceInfo) {
  const resource = yield call(getResourceQuery, spaceInfo);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSearchSpaceInfo(
  spaceInfo: SpaceInfo
) {
  // if the current search term in empty, return 0
  const mixedContentFilter: FilterMap = yield select(
    selectMixedContentFilterClauses
  );
  if (
    _.isEmpty(mixedContentFilter) ||
    !mixedContentFilter.search ||
    ((mixedContentFilter.search as IStringValuedFilterClause).value.length === 0 && !FeatureFlags.searchQuickLinks))
    {
    return 0;
  }

  const resource = yield call(getResourceQuery, spaceInfo);
  return yield call(processContentCountRequest, resource);
}

export function* handleContentCountsRequest(
  action: ContentCountsRequestAction
) {
  try {
    const isUpToDate = yield select(selectContentCountsUpToDate);
    if (!isUpToDate) {
      const flowsEnabled: boolean = yield select(selectFlowsOnSiteEnabled);
      const catalogingOnSiteEnabled: boolean = yield select(
        selectCatalogingOnSiteEnabled
      );
      const areUsersVisible: boolean = !(yield select(
        CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced
      ));
      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      const counts: SpaceInfoTypeContentCounts = yield all(
        getContentCountsRequests(
          spaceInfo,
          flowsEnabled,
          catalogingOnSiteEnabled,
          areUsersVisible
        )
      );
      yield put(contentCountsRequestSucceeded(spaceInfo.spaceType, counts));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function getContentCountsRequests(
  spaceInfo: SpaceInfo,
  flowsEnabled: boolean,
  catalogingOnSiteEnabled: boolean,
  areUsersVisible: boolean
): Partial<Record<ExploreContentApi_ContentType | SubspaceName, CallEffect>> {
  const pConnsEnabled: boolean =
    FeatureFlags.publishedConnectionContentManagementEnabled;
  const predictiveModelEnabled: boolean = FeatureFlags.prometheusPredictiveModelEnabled;
  if (isSiteSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<SiteSpaceContentType, CallEffect> =
      {
        explore: call(processContentCountRequestForSite, 'explore'),
        project: call(processContentCountRequestForSite, 'project'),
        workbook: call(processContentCountRequestForSite, 'workbook'),
        datasource: call(processContentCountRequestForSite, 'datasource'),
        dataRole: call(processContentCountRequestForSite, 'dataRole'),
        lens: call(processContentCountRequestForSite, 'lens'),
        view: call(processContentCountRequestForSite, 'view'),
        flow: flowsEnabled
          ? call(processContentCountRequestForSite, 'flow')
          : call(zero),
        dataconnection: call(zero),
        databaseWithCrass: call(zero),
        tableWithCrass: call(zero),
        database: call(zero),
        table: call(zero),
        collection: call(zero),
        metric: FeatureFlags.metricsSpaceEnabled
          ? call(processContentCountRequestForSite, 'metric')
          : call(zero),
        virtualConnection: pConnsEnabled
          ? call(processContentCountRequestForSite, 'virtualConnection')
          : call(zero),
        virtualConnectionTable: call(zero),
        virtualConnectionColumn: call(zero),
        predictiveModel: predictiveModelEnabled
          ? call(processContentCountRequestForSite, 'predictiveModel')
          : call(zero),
      };
    return counts;
  } else if (isProjectSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(processContentCountRequestForProject, 'project'),
      workbook: call(processContentCountRequestForProject, 'workbook'),
      datasource: call(processContentCountRequestForProject, 'datasource'),
      dataRole: call(processContentCountRequestForProject, 'dataRole'),
      lens: call(processContentCountRequestForProject, 'lens'),
      view: call(processContentCountRequestForProject, 'view'),
      flow: flowsEnabled
        ? call(processContentCountRequestForProject, 'flow')
        : call(zero),
      dataconnection: call(zero),
      databaseWithCrass: call(zero),
      tableWithCrass: call(zero),
      database: call(zero),
      table: call(zero),
      collection: call(zero),
      metric: FeatureFlags.metricsSpaceEnabled
        ? call(processContentCountRequestForProject, 'metric')
        : call(zero),
      virtualConnection: pConnsEnabled
        ? call(processContentCountRequestForProject, 'virtualConnection')
        : call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      predictiveModel: predictiveModelEnabled
      ? call(processContentCountRequestForProject, 'predictiveModel')
      : call(zero),
    };
    return counts;
  } else if (isFavoritesSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(processContentCountRequestForFavorites, 'project'),
      workbook: call(processContentCountRequestForFavorites, 'workbook'),
      datasource: call(processContentCountRequestForFavorites, 'datasource'),
      dataRole: call(processContentCountRequestForFavorites, 'dataRole'),
      lens: call(processContentCountRequestForFavorites, 'lens'),
      view: call(processContentCountRequestForFavorites, 'view'),
      flow: flowsEnabled
        ? call(processContentCountRequestForFavorites, 'flow')
        : call(zero),
      dataconnection: call(zero),
      databaseWithCrass: call(zero),
      tableWithCrass: call(zero),
      database: call(zero),
      table: call(zero),
      metric: FeatureFlags.metricsSpaceEnabled
        ? call(processContentCountRequestForFavorites, 'metric')
        : call(zero),
      virtualConnection: pConnsEnabled
        ? call(processContentCountRequestForFavorites, 'virtualConnection')
        : call(zero),
      virtualConnectionTable: pConnsEnabled
        ? call(processContentCountRequestForFavorites, 'virtualConnectionTable')
        : call(zero),
      virtualConnectionColumn: call(zero),
      collection: FeatureFlags.useCollectionsFavorites
        ? call(processContentCountRequestForFavorites, 'collection')
        : call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForFavorites, 'predictiveModel')
        : call(zero),
    };
    return counts;
  } else if (isWorkbookSpace(spaceInfo)) {
    const counts: Record<WorkbookSubspaceName, CallEffect> = {
      datasources: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'datasources',
      }),
      views: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'views',
      }),
      customViews: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'customViews',
      }),
      subscriptions: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'subscriptions',
      }),
      dataconnections: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'dataconnections',
      }),
      extractTasks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'extractTasks',
      }),
      connectedMetrics: FeatureFlags.metricsSpaceEnabled
        ? call(processContentCountRequestForSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'connectedMetrics',
          })
        : call(zero),
      lineage: call(zero),
      workbookAccelerations: FeatureFlags.workbookAccelerationUIEnabled
        ? call(processContentCountRequestForSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'workbookAccelerations',
          })
        : call(zero),
    };
    return counts;
  } else if (isDatasourceSpace(spaceInfo)) {
    const counts: Record<DatasourceSubspaceName, CallEffect> = {
      lineage: call(zero),
      nlp: call(zero),
      connections: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connections',
      }),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'extractRefreshes',
      }),
      remoteRefreshSchedules: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'remoteRefreshSchedules',
      }),
      connectedWorkbooks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connectedWorkbooks',
      }),
      askDataUsageAnalytics: call(zero),
    };
    return counts;
  } else if (isSearchSpace(spaceInfo)) {
    const counts: Record<SearchSubspaceName, CallEffect> = {
      views: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'views',
      }),
      metrics: FeatureFlags.metricsSpaceEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'metrics',
          })
        : call(zero),
      workbooks: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'workbooks',
      }),
      datasources: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'datasources',
      }),
      flows: flowsEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'flows',
          })
        : call(zero),
      projects: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'projects',
      }),
      tables: call(zero),
      databases: call(zero),
      tablesWithCrass:
        catalogingOnSiteEnabled
          ? call(processContentCountRequestForSearchSpaceInfo, {
              ...spaceInfo,
              subspaceName: 'tablesWithCrass',
            })
          : call(zero),
      databasesWithCrass:
        catalogingOnSiteEnabled
          ? call(processContentCountRequestForSearchSpaceInfo, {
              ...spaceInfo,
              subspaceName: 'databasesWithCrass',
            })
          : call(zero),
      dataRoles: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'dataRoles',
      }),
      lenses: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'lenses',
      }),
      users: areUsersVisible
        ? call(processContentCountRequestForSearchSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'users',
          })
        : call(zero),
      virtualConnections: pConnsEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'virtualConnections',
          })
        : call(zero),
      predictiveModels: predictiveModelEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'predictiveModels',
          })
        : call(zero),
    };
    return counts;
  } else if (isSiteUserSpace(spaceInfo)) {
    const counts: Record<
      SiteUserSubspaceName | ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(
        processContentCountRequestForSiteUserContentType,
        'project'
      ),
      workbook: call(
        processContentCountRequestForSiteUserContentType,
        'workbook'
      ),
      view: call(processContentCountRequestForSiteUserContentType, 'view'),
      metric: FeatureFlags.metricsSpaceEnabled
        ? call(processContentCountRequestForSiteUserContentType, 'metric')
        : call(zero),
      datasource: call(
        processContentCountRequestForSiteUserContentType,
        'datasource'
      ),
      flow: flowsEnabled
        ? call(processContentCountRequestForSiteUserContentType, 'flow')
        : call(zero),
      dataRole: call(
        processContentCountRequestForSiteUserContentType,
        'dataRole'
      ),
      lens: call(processContentCountRequestForSiteUserContentType, 'lens'),
      virtualConnection: pConnsEnabled
        ? call(
            processContentCountRequestForSiteUserContentType,
            'virtualConnection'
          )
        : call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      dataconnection: call(zero),
      databaseWithCrass: call(zero),
      tableWithCrass: call(zero),
      database: call(zero),
      table: call(zero),
      content: call(zero),
      collection: call(zero),
      alerts: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'alerts',
      }),
      collections: FeatureFlags.collectionsEnabled
        ? call(processContentCountRequestForSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'collections',
          })
        : call(zero),
      subscriptions: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'subscriptions',
      }),
      settings: call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForSiteUserContentType, 'predictiveModel')
        : call(zero),
    };
    return counts;
  } else if (isDataRoleSpace(spaceInfo)) {
    const counts: Record<DataRoleSubspaceName, CallEffect> = {
      definition: call(zero),
      connectedFlows: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connectedFlows',
      }),
      connectedDatasources: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connectedDatasources',
      }),
    };
    return counts;
  } else if (isScheduleSpace(spaceInfo)) {
    const counts: Record<ScheduleSubspaceName, CallEffect> = {
      details: call(zero),
      subscriptions: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'subscriptions',
      }),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'extractRefreshes',
      }),
      flows: flowsEnabled
        ? call(processContentCountRequestForSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'flows',
          })
        : call(zero),
    };
    return counts;
  } else if (isSiteTasksSpace(spaceInfo)) {
      const counts: Record<SiteTasksSubspaceName, CallEffect> = {
        dataAcceleration: FeatureFlags.accelerationSiteAdminUIEnabled
          ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'dataAcceleration'}) : call(zero),
        extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
        runFlowTasks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'runFlowTasks'}),
        subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
        alerts: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'alerts'}),
        linkedTasks: areLinkedTasksEnabled() ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'linkedTasks'}): call(zero),
      };
      return counts;
  } else if (isServerTasksSpace(spaceInfo)) {
    const counts: Record<ServerTasksSubspaceName, CallEffect> = {
      dataAcceleration: FeatureFlags.accelerationSiteAdminUIEnabled
        ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'dataAcceleration'}) : call(zero),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
      runFlowTasks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'runFlowTasks'}),
      subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
      linkedTasks: areLinkedTasksEnabled() ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'linkedTasks'}): call(zero),
    };
    return counts;
  } else if (isSharedWithMeSpace(spaceInfo)) {
    // const counts: SpaceInfoTypeContentCount<ExploreContentApi_ContentType, CallEffect> = {
    //   project: call(processContentCountRequestForSharedWithMe, 'project'),
    //   workbook: call(processContentCountRequestForSharedWithMe, 'workbook'),
    //   datasource: call(processContentCountRequestForSharedWithMe, 'datasource'),
    //   dataRole: call(zero),
    //   view: call(processContentCountRequestForSharedWithMe, 'view'),
    //   flow: flowsEnabled ? call(processContentCountRequestForSharedWithMe, 'flow') : call(zero),
    //   dataconnection: call(zero),
    //   database: call(zero),
    //   table: call(zero),
    //   metric: FeatureFlags.metricsSpaceEnabled ? call(processContentCountRequestForSharedWithMe, 'metric') : call(zero),
    //   virtualConnection: call(zero),
    //   collection: call(zero),
    // };
    // TFSID 1119981 Temporarily disable count calculation logic.
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(zero),
      workbook: call(zero),
      datasource: call(zero),
      dataRole: call(zero),
      lens: call(zero),
      view: call(zero),
      flow: call(zero),
      dataconnection: call(zero),
      databaseWithCrass: call(zero),
      tableWithCrass: call(zero),
      database: call(zero),
      table: call(zero),
      metric: call(zero),
      virtualConnection: call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: call(zero),
    };
    return counts;
  } else if (isPersonalSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(zero),
      workbook: call(
        processContentCountRequestForPersonalSpaceContentType,
        'workbook'
      ),
      datasource: call(
        processContentCountRequestForPersonalSpaceContentType,
        'datasource'
      ),
      dataRole: call(zero),
      lens: call(zero),
      view: call(zero),
      flow: call(zero),
      dataconnection: call(zero),
      databaseWithCrass: call(zero),
      tableWithCrass: call(zero),
      database: call(zero),
      table: call(zero),
      metric: call(zero),
      virtualConnection: call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: call(zero),
    };
    return counts;
  } else if (
    isSingleCollectionSpace(spaceInfo) &&
    FeatureFlags.collectionsEnabled &&
    FeatureFlags.collectionsGAEnabled
  ) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(
        processContentCountRequestForCollectionSpaceContentType,
        'project'
      ),
      workbook: call(
        processContentCountRequestForCollectionSpaceContentType,
        'workbook'
      ),
      datasource: call(
        processContentCountRequestForCollectionSpaceContentType,
        'datasource'
      ),
      dataRole: call(
        processContentCountRequestForCollectionSpaceContentType,
        'dataRole'
      ),
      lens: call(zero),
      view: call(
        processContentCountRequestForCollectionSpaceContentType,
        'view'
      ),
      flow: flowsEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'flow')
        : call(zero),
      dataconnection: call(zero),
      databaseWithCrass: call(zero),
      tableWithCrass: call(zero),
      database: call(zero),
      table: call(zero),
      metric: FeatureFlags.metricsSpaceEnabled
        ? call(
            processContentCountRequestForCollectionSpaceContentType,
            'metric'
          )
        : call(zero),
      virtualConnection: pConnsEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'virtualConnection')
        : call(zero),
      virtualConnectionTable: pConnsEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'virtualConnectionTable')
        : call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: predictiveModelEnabled
          ? call(processContentCountRequestForCollectionSpaceContentType, 'predictiveModel')
          : call(zero),
    };
    return counts;
  } else if (isCatalogSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(zero),
      workbook: call(zero),
      datasource: call(zero),
      dataRole: call(zero),
      lens: call(zero),
      view: call(zero),
      flow: call(zero),
      dataconnection: call(zero),
      databaseWithCrass: catalogingOnSiteEnabled ? call(processContentCountRequestForCatalog, 'databaseWithCrass') : call(zero),
      tableWithCrass: catalogingOnSiteEnabled ? call(processContentCountRequestForCatalog, 'tableWithCrass') : call(zero),
      database: call(zero),
      table: call(zero),
      metric: call(zero),
      virtualConnection: call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: call(zero),
    };
    return counts;
  } else if (isPublishedConnectionSpace(spaceInfo)) {
    const counts: Record<PublishedConnectionSubspaceName, CallEffect> = {
      scheduledTasks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'scheduledTasks',
      }),
      tables: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'tables',
      }),
      lineage: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'lineage',
      }),
    };
    return counts;
  } else if (isPublishedConnectionTableSpace(spaceInfo)) {
    const counts: Record<PublishedConnectionTableSubspaceName, CallEffect> = {
      scheduledTasks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'scheduledTasks',
      }),
      overview: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'overview',
      }),
      lineage: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'lineage',
      }),
    };
    return counts;
  }
}

function* zero() {
  return 0;
}
